import React, { Component } from 'react';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { lazyLoadWithDimensions } from '../../util/contextHelpers';
import { LINE_ITEM_DAY, LINE_ITEM_NIGHT } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug, decodeLatLng } from '../../util/urlHelpers';
import { getDistanceInKilometers } from '../../util/maps';
import config from '../../config';
import { IconPinMap, NamedLink, ResponsiveImage } from '../../components';
import { ExperimentId, ExperimentVariant } from '../../abTest';
import { useExperiment } from '../../hooks/useExperiment.hook';

import css from './ListingCard.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

// Funkcja pomocnicza do obliczania różnicy dni między dwoma datami (+1 dzień)
const getDaysCountFromDates = (startDateStr, endDateStr) => {
  if (!startDateStr || !endDateStr) return 1;

  const start = new Date(startDateStr);
  const end = new Date(endDateStr);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;

  const diffInMs = end.getTime() - start.getTime();
  const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

  return diffInDays >= 0 ? diffInDays + 1 : 1;
};

const getCoordinatesOfNearestStore = (deliveryToProviders, point) => {
  const distances = [];

  deliveryToProviders.forEach(item => {
    const lat = item.attributes?.location?.lat;
    const lng = item.attributes?.location?.lng;
    const distance =
      lat && lng && Math.round(getDistanceInKilometers({ lat, lng }, point) * 10) / 10;
    distances.push(distance);
  });

  const sortedDistances = distances.sort((a, b) => a - b);
  return sortedDistances[0];
};

const priceData = (price, intl) => {
  if (price) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

class ListingImage extends Component {
  render() {
    return <ResponsiveImage {...this.props} />;
  }
}
const LazyImage = lazyLoadWithDimensions(ListingImage, { loadAfterInitialRendering: 3000 });

export const ListingCardComponent = ({
  className,
  rootClassName,
  intl,
  listing,
  renderSizes = null,
  locationCenter,
  isUserLocation = false,
  pageName,
}) => {
  const classes = classNames(rootClassName || css.root, className);
  const location = useLocation();

  // Pobieranie start i end z query params (URL location.search)
  const searchParams = new URLSearchParams(location.search);
  const startParam = searchParams.get('start');
  const endParam = searchParams.get('end');

  // Obliczenie liczby dni na podstawie parametry start/end
  const daysCount = getDaysCountFromDates(startParam, endParam);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price } = currentListing.attributes;
  const isDeliveryToProviders = !!listing.provider.deliveryToProviders?.length;
  const point = locationCenter && decodeLatLng(locationCenter);
  const { lat, lng } = listing.provider?.attributes.location || {};
  let distance = false;

  if (isDeliveryToProviders) {
    distance = point && getCoordinatesOfNearestStore(listing.provider.deliveryToProviders, point);
  } else {
    distance =
      point && lat && lng && Math.round(getDistanceInKilometers({ lat, lng }, point) * 10) / 10;
  }

  const slug = createSlug(title);
  const author = ensureUser(listing.provider);
  const authorName = author.attributes && author.attributes.name;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const { formattedPrice, priceTitle } = priceData(price, intl);
  const { formattedPrice: testPrice, priceTitle: testPriceTitle } = priceData(
    { amount: currentListing.attributes?.perPeriod?.price || price?.amount, currency: price?.currency },
    intl
  );

  const isSubscriptionProduct = pageName === 'SubscriptionSearchPage';

  // Obsługa jednostki ceny w zależności od rodzaju produktu
  const isNightly = currentListing.attributes?.unitType === LINE_ITEM_NIGHT;
  const isDaily = currentListing.attributes?.unitType === LINE_ITEM_DAY;
  console.log(isDaily)

  const unitTranslationKey = isSubscriptionProduct ? 'ListingCard.month' : 'ListingCard.day';

  const distanceText = isUserLocation ? 'ListingCard.distanceFromYou' : 'ListingCard.distance';
  const linkPageName = isSubscriptionProduct ? 'SubscriptionListingPage' : 'ListingPage';

  // Eksperyment A/B z ceną dla standardowych produktów
  const { Variant: TotalPriceVariant } = useExperiment({
    id: ExperimentId.TOTAL_PRICE,
    variants: {
      [ExperimentVariant.CONTROL]: (
        <div className={css.price}>
          <div className={css.priceValue} title={priceTitle}>
            {formattedPrice}
          </div>
          <div className={css.perUnit}>
            / <FormattedMessage id={unitTranslationKey} values={{ count: 1 }} />
          </div>
        </div>
      ),
      [ExperimentVariant.VARIANT_A]: (
        <div className={css.price}>
          <div className={css.priceValue} title={priceTitle}>
            {testPrice}
          </div>
          <div className={classNames(css.perUnit, css.test)}>
            / <FormattedMessage id={unitTranslationKey} values={{ count: daysCount }} />
          </div>
        </div>
      ),
      [ExperimentVariant.VARIANT_B]: (
        <div className={css.priceWrapper}>
          <div className={css.price}>
            <div className={css.priceValue} title={priceTitle}>
              {formattedPrice}
            </div>
            <div className={css.perUnit}>
              / <FormattedMessage id={unitTranslationKey} values={{ count: 1 }} />
            </div>
          </div>

          {daysCount > 1 && (
            <div className={css.totalPriceSecondary}>
              <FormattedMessage
                id="total.price.period"
                defaultMessage="{price} / {count} dni"
                values={{
                  price: testPrice,
                  count: daysCount,
                }}
              />
            </div>
          )}
        </div>
      ),
    },
  });

  return (
    <NamedLink className={classes} name={linkPageName} params={{ id, slug }}>
      <div className={css.threeToTwoWrapper}>
        <div className={css.aspectWrapper}>
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={['scaled-medium']}
            sizes={renderSizes}
          />
        </div>
      </div>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          <div className={css.authorInfo}>
            <FormattedMessage id="ListingCard.hostedBy" values={{ authorName }} />
          </div>
        </div>

        {/* Jeśli subskrypcja – renderujemy widok cenowy subskrypcji, w przeciwnym wypadku eksperyment A/B */}
        {isSubscriptionProduct ? (
          <>
            <div className={css.price}>
              <div className={css.priceValue} title={priceTitle}>
                {formattedPrice}
              </div>
              <div className={css.perUnit}>
                / <FormattedMessage id={unitTranslationKey} />
              </div>
            </div>
            <div className={css.subscriptionInfoInline}>
              <span className={css.subscriptionTextSmall}>
                <FormattedMessage
                  id="ListingCard.minDuration"
                  values={{ count: currentListing.attributes?.minDurationMonths }}
                />
              </span>
            </div>
          </>
        ) : (
          TotalPriceVariant
        )}

        {(distance || distance === 0) && (
          <div className={css.distanceContainer}>
            <IconPinMap />
            <p className={css.distanceInfo}>
              <FormattedMessage id={distanceText} values={{ distance }} />
            </p>
          </div>
        )}
      </div>
    </NamedLink>
  );
};

export default injectIntl(ListingCardComponent);